/**
 * Knowledge module API client — typed wrappers over `apiRequest` for the
 * /api/knowledge/* surface (sc-platform `routes/knowledge/routes.ts`).
 * Shapes mirror the platform's response interfaces; the API is the source
 * of truth and enforces scopes on every call.
 */
import { apiRequest } from '../../lib/apiClient.js';
import { agentBaseUrl } from '../../lib/agentApi.js';

export type KnowledgeStageMode = 'MANUAL' | 'LLM_SYNTHESIS' | 'LLM_DEBATE';
export type KnowledgeStrictness = 'STRICT' | 'OPEN';

export interface KnowledgeStage {
  mode: KnowledgeStageMode;
  prompt: string;
  strictness: KnowledgeStrictness;
}

export interface KnowledgeTemplate {
  id: string;
  name: string;
  description: string | null;
  promptContext: string;
  subjectNoun: string;
  itemNoun: string;
  list: KnowledgeStage;
  item: KnowledgeStage;
  subjectCount: number;
}

export interface UpdateTemplateBody {
  name?: string;
  description?: string | null;
  promptContext?: string;
  subjectNoun?: string;
  itemNoun?: string;
  list?: Partial<KnowledgeStage>;
  item?: Partial<KnowledgeStage>;
}

export interface KnowledgeItemRevision {
  id: string;
  version: number;
  title: string;
  bodyMarkdown: string;
  editedByEmail: string;
  editSource: 'GENERATION' | 'REGENERATION' | 'MANUAL' | 'FEEDBACK_RESOLUTION' | 'IMPORT';
  editNote: string | null;
  createdAt: string;
}

export interface KnowledgeDocument {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  uploadedByEmail: string;
  createdAt: string;
  /** null = the default bucket. */
  bucketId: string | null;
  /** The equipment it applies to; empty = every subject. `archived` = deleted equipment. */
  subjects: Array<{ id: string; name: string; archived?: boolean }>;
}

export interface KnowledgeSubjectImportResult {
  created: KnowledgeSubject[];
  skipped: string[];
  errors: Array<{ name: string; message: string }>;
}

export interface KnowledgeSubject {
  id: string;
  templateId: string;
  templateName: string;
  subjectNoun: string;
  itemNoun: string;
  listMode: KnowledgeStageMode;
  itemMode: KnowledgeStageMode;
  entityId: string | null;
  entityName: string | null;
  aliases: string[];
  name: string;
  kind: string | null;
  notes: string | null;
  promptContext: string | null;
  archived: boolean;
  itemCount: number;
  documentCount: number;
  updatedAt: string;
}

export type KnowledgeItemStatus =
  | 'DRAFT'
  | 'PUBLISHED'
  | 'ARCHIVED'
  | 'GENERATING'
  | 'GENERATION_FAILED';

export interface KnowledgeItem {
  id: string;
  subjectId: string;
  ordinal: number;
  title: string;
  bodyMarkdown: string;
  status: KnowledgeItemStatus;
  version: number;
  errorMessage: string | null;
  publishedAt: string | null;
  hasUnpublishedChanges: boolean;
  updatedAt: string;
}

export type GenerationRunStatus = 'PENDING' | 'RUNNING' | 'COMPLETE' | 'ERROR' | 'CANCELLED';

export interface GenerationRun {
  id: string;
  subjectId: string;
  kind: 'LIST' | 'ITEMS' | 'ITEM';
  status: GenerationRunStatus;
  attempt: number;
  cancelRequested: boolean;
  message: string | null;
  errorMessage: string | null;
  listOutput: { titles?: string[] } | null;
  /** Task tallies — null until the worker has created the run's tasks. */
  progress: { total: number; done: number; failed: number } | null;
  requestedByEmail: string;
  createdAt: string;
  finishedAt: string | null;
}

export interface KnowledgeRule {
  id: string;
  title: string;
  bodyMarkdown: string;
  status: 'DRAFT' | 'PUBLISHED';
  publishedAt: string | null;
  hasUnpublishedChanges: boolean;
  subjects: Array<{ id: string; name: string }>;
  /** null = the default bucket. */
  bucketId: string | null;
  updatedAt: string;
}

export interface KnowledgeEntity {
  id: string;
  name: string;
  aliases: string[];
  source: 'ROSTER' | 'MANUAL';
  subjectCount: number;
}

export interface KnowledgeBucket {
  id: string;
  name: string;
  isDefault: boolean;
  /** Groups whose members may read this bucket. Empty = everyone who can
   *  search the knowledge base. */
  readGroups: Array<{ id: string; name: string }>;
  ruleCount: number;
  documentCount: number;
  createdAt: string;
  updatedAt: string;
}

export const knowledgeApi = {
  templates: () => apiRequest<KnowledgeTemplate[]>('/api/knowledge/templates'),
  subjects: () => apiRequest<KnowledgeSubject[]>('/api/knowledge/subjects'),
  subject: (id: string) => apiRequest<KnowledgeSubject>(`/api/knowledge/subjects/${id}`),
  createSubject: (body: { templateId: string; name: string; kind?: string | null }) =>
    apiRequest<KnowledgeSubject>('/api/knowledge/subjects', { method: 'POST', body }),
  updateTemplate: (id: string, body: UpdateTemplateBody) =>
    apiRequest<KnowledgeTemplate>(`/api/knowledge/templates/${id}`, { method: 'PATCH', body }),
  draftPrompt: (body: {
    kind: 'briefing' | 'list' | 'item';
    gist: string;
    categoryName?: string;
    briefing?: string;
    subjectNoun?: string;
    itemNoun?: string;
  }) =>
    apiRequest<{ text: string }>('/api/knowledge/assist/draft-prompt', { method: 'POST', body }),
  previewPrompt: (body: {
    stage: 'list' | 'item';
    promptContext: string;
    prompt: string;
    strictness: KnowledgeStrictness;
    subjectNoun: string;
    itemNoun: string;
  }) =>
    apiRequest<{ text: string }>('/api/knowledge/assist/preview-prompt', { method: 'POST', body }),
  suggestCategory: (description: string) =>
    apiRequest<{ name: string; subjectNoun: string; itemNoun: string; promptContext: string }>(
      '/api/knowledge/assist/suggest-category',
      { method: 'POST', body: { description } },
    ),
  createTemplate: (body: {
    name: string;
    subjectNoun: string;
    itemNoun: string;
    description?: string | null;
    promptContext?: string;
    list?: Partial<KnowledgeStage>;
    item?: Partial<KnowledgeStage>;
  }) => apiRequest<KnowledgeTemplate>('/api/knowledge/templates', { method: 'POST', body }),
  importSubjects: (body: {
    templateId: string;
    rows: Array<{ name: string; kind?: string | null }>;
  }) =>
    apiRequest<KnowledgeSubjectImportResult>('/api/knowledge/subjects/import', {
      method: 'POST',
      body,
    }),
  restoreRevision: (itemId: string, version: number) =>
    apiRequest<KnowledgeItem>(
      `/api/knowledge/items/${itemId}/revisions/${String(version)}/restore`,
      { method: 'POST', body: {} },
    ),
  revisions: (itemId: string) =>
    apiRequest<KnowledgeItemRevision[]>(`/api/knowledge/items/${itemId}/revisions`),
  documents: (subjectId: string) =>
    apiRequest<KnowledgeDocument[]>(`/api/knowledge/subjects/${subjectId}/documents`),
  buckets: () => apiRequest<KnowledgeBucket[]>('/api/knowledge/buckets'),
  /** The tenant's groups, for bucket audience pickers (same endpoint the
   *  Groups page uses). */
  groups: () =>
    apiRequest<{ groups: Array<{ id: string; name: string; memberCount: number }> }>('/api/groups'),
  createBucket: (body: { name: string; readGroupIds: string[] }) =>
    apiRequest<KnowledgeBucket>('/api/knowledge/buckets', { method: 'POST', body }),
  updateBucket: (id: string, body: { name?: string; readGroupIds?: string[] }) =>
    apiRequest<KnowledgeBucket>(`/api/knowledge/buckets/${id}`, { method: 'PATCH', body }),
  deleteBucket: (id: string) =>
    apiRequest<void>(`/api/knowledge/buckets/${id}`, { method: 'DELETE' }),
  generalDocuments: () => apiRequest<KnowledgeDocument[]>('/api/knowledge/documents'),
  document: (id: string) => apiRequest<KnowledgeDocument>(`/api/knowledge/documents/${id}`),
  setDocumentSubjects: (id: string, subjectIds: string[]) =>
    apiRequest<KnowledgeDocument>(`/api/knowledge/documents/${id}/subjects`, {
      method: 'PUT',
      body: { subjectIds },
    }),
  uploadGeneralDocument: (file: File, bucketId?: string) => {
    const form = new FormData();
    form.append('file', file);
    const qs = bucketId === undefined ? '' : `?bucketId=${encodeURIComponent(bucketId)}`;
    return apiRequest<KnowledgeDocument>(`/api/knowledge/documents${qs}`, {
      method: 'POST',
      body: form,
    });
  },
  extractRule: (file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiRequest<{ text: string }>('/api/knowledge/rules/extract', {
      method: 'POST',
      body: form,
    });
  },
  uploadDocument: (subjectId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiRequest<KnowledgeDocument>(`/api/knowledge/subjects/${subjectId}/documents`, {
      method: 'POST',
      body: form,
    });
  },
  deleteDocument: (id: string) =>
    apiRequest<void>(`/api/knowledge/documents/${id}`, { method: 'DELETE' }),
  createItem: (subjectId: string, body: { title: string }) =>
    apiRequest<KnowledgeItem>(`/api/knowledge/subjects/${subjectId}/items`, {
      method: 'POST',
      body,
    }),
  deleteSubject: (id: string) =>
    apiRequest<{ items: number; documents: number }>(`/api/knowledge/subjects/${id}`, {
      method: 'DELETE',
    }),
  resetSubject: (id: string) =>
    apiRequest<{ unpublished: number; runsCleared: number }>(
      `/api/knowledge/subjects/${id}/reset`,
      { method: 'POST' },
    ),
  createEntity: (body: { name: string; aliases?: string[] }) =>
    apiRequest<KnowledgeEntity>('/api/knowledge/entities', { method: 'POST', body }),
  updateSubject: (
    id: string,
    body: {
      notes?: string | null;
      promptContext?: string | null;
      kind?: string | null;
      entityId?: string | null;
    },
  ) => apiRequest<KnowledgeSubject>(`/api/knowledge/subjects/${id}`, { method: 'PATCH', body }),
  items: (subjectId: string) =>
    apiRequest<KnowledgeItem[]>(`/api/knowledge/subjects/${subjectId}/items`),
  item: (id: string) => apiRequest<KnowledgeItem>(`/api/knowledge/items/${id}`),
  updateItem: (id: string, body: { bodyMarkdown?: string; title?: string; editNote?: string }) =>
    apiRequest<KnowledgeItem>(`/api/knowledge/items/${id}`, { method: 'PATCH', body }),
  deleteItem: (id: string) => apiRequest<void>(`/api/knowledge/items/${id}`, { method: 'DELETE' }),
  approveItem: (id: string, note?: string) =>
    apiRequest<KnowledgeItem>(`/api/knowledge/items/${id}/approve`, {
      method: 'POST',
      body: note ? { note } : {},
    }),
  // ─── Runs live on ORCHESTRATION, not the API ────────────────────────────
  // Starting a run means writing guides, which needs the model SDKs — so the
  // process that does the work owns the endpoints that start, watch and stop
  // it. Same host the chat already calls, same token; only the base URL
  // differs. Content endpoints above stay on the API.
  runs: (subjectId?: string) =>
    apiRequest<GenerationRun[]>(
      subjectId ? `/api/knowledge/runs?subjectId=${subjectId}` : '/api/knowledge/runs',
      { baseUrl: agentBaseUrl },
    ),
  enqueueRun: (body: { subjectId: string; kind: 'LIST' | 'ITEMS' | 'ITEM'; itemId?: string }) =>
    apiRequest<GenerationRun>('/api/knowledge/runs', {
      method: 'POST',
      body,
      baseUrl: agentBaseUrl,
    }),
  cancelRun: (id: string) =>
    apiRequest<GenerationRun>(`/api/knowledge/runs/${id}/cancel`, {
      method: 'POST',
      body: {},
      baseUrl: agentBaseUrl,
    }),
  /** What the DEPLOYMENT supports — distinct from what this user may do. */
  capabilities: () =>
    apiRequest<{ documents: boolean; publishing: boolean }>('/api/knowledge/capabilities'),
  entities: () => apiRequest<KnowledgeEntity[]>('/api/knowledge/entities'),
  updateEntity: (id: string, body: { name?: string; aliases?: string[] }) =>
    apiRequest<KnowledgeEntity>(`/api/knowledge/entities/${id}`, { method: 'PATCH', body }),
  rules: () => apiRequest<KnowledgeRule[]>('/api/knowledge/rules'),
  createRule: (body: {
    title: string;
    bodyMarkdown: string;
    subjectIds?: string[];
    bucketId?: string;
  }) => apiRequest<KnowledgeRule>('/api/knowledge/rules', { method: 'POST', body }),
  updateRule: (
    id: string,
    body: { title?: string; bodyMarkdown?: string; subjectIds?: string[] },
  ) => apiRequest<KnowledgeRule>(`/api/knowledge/rules/${id}`, { method: 'PATCH', body }),
  publishRule: (id: string) =>
    apiRequest<KnowledgeRule>(`/api/knowledge/rules/${id}/publish`, { method: 'POST', body: {} }),
  deleteRule: (id: string) => apiRequest<void>(`/api/knowledge/rules/${id}`, { method: 'DELETE' }),
  materializeRun: (id: string, titles: string[]) =>
    apiRequest<{ created: number; generating: boolean }>(`/api/knowledge/runs/${id}/materialize`, {
      method: 'POST',
      body: { titles },
      baseUrl: agentBaseUrl,
    }),
};
